import { Link } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { Camera, Fuel, Gauge, Heart, MapPin, Settings2, TrendingDown } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { morphName, setMorphTarget, useMorphTarget } from '@/lib/morph';
import { formatMileage, formatPrice, formatRelative } from '@/lib/format';
import { useAuth, useMakeLookup, useReference } from '@/hooks/useApp';
import { cn } from '@/lib/cn';
import { Badge } from '@/components/ui';
import { BrandMark } from '@/components/BrandMark';
import { CarSilhouette } from '@/components/CarSilhouette';
import { Photo } from '@/components/Photo';
import { RatingBadge } from '@/components/StarRating';
import type { ListingSummary } from '@/lib/types';

function labelFor(options: { value: number; label: string }[] | undefined, value: number) {
  return options?.find((option) => option.value === value)?.label ?? '';
}

interface Props {
  listing: ListingSummary;
  /** Position in the grid, used only to stagger the entry animation. */
  index?: number;
}

export function CarCard({ listing, index = 0 }: Props) {
  const { user } = useAuth();
  const { data: reference } = useReference();
  const makes = useMakeLookup();
  const queryClient = useQueryClient();

  const toggleFavourite = useMutation({
    mutationFn: () => api(`/api/listings/${listing.id}/favourite`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['favourites'] });
    },
  });

  const priceDropped = listing.previousPrice != null && listing.previousPrice > listing.price;
  const make = makes.get(listing.makeName) ?? { name: listing.makeName };

  // Paired with the same name on the detail page's hero, so the photo grows out of the card into
  // the page instead of the two screens cutting. Only ever one card at a time.
  const morphing = useMorphTarget() === listing.shortCode;

  return (
    <article
      // Capped so the last card in a long page does not wait two seconds to appear.
      style={{ '--i': Math.min(index, 11) } as React.CSSProperties}
      className={cn(
        'group animate-fade-in-up stagger lift relative isolate overflow-hidden rounded-[var(--radius-card)]',
        'border border-default bg-raised shadow-card hover:border-strong hover:shadow-card-hover',
      )}
    >
      <Link
        to={`/oglas/${listing.shortCode}`}
        viewTransition
        /*
          Claim the morph before navigating, and flush it, so the photograph already carries the
          name when the browser takes its snapshot. React would otherwise batch this update until
          after `navigate` has started the transition, and the card would be captured unnamed.
        */
        onClick={() => flushSync(() => setMorphTarget(listing.shortCode))}
        className="block"
      >
        <div
          className="relative aspect-[4/3] overflow-hidden bg-sunken"
          style={morphing ? { viewTransitionName: morphName(listing.shortCode) } : undefined}
        >
          <Photo
            src={listing.thumbnailUrl}
            alt={`${listing.makeName} ${listing.modelName}`}
            className="size-full"
            imageClassName="transition duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105"
            fallback={
              <span className="flex flex-col items-center gap-2 text-faint">
                <CarSilhouette bodyType={listing.bodyType} className="w-20 opacity-40" />
                <span className="text-[11px]">Bez fotografije</span>
              </span>
            }
          />

          {/*
            A gradient scrim rather than a solid bar, and only over a real photograph: the overlaid
            badges need contrast, and a scrim gives it without cropping any of the image the seller
            chose. Over the empty-state placeholder it would just be a dark smear on grey.
          */}
          {listing.thumbnailUrl && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/55 to-transparent"
            />
          )}

          <div className="pointer-events-none absolute inset-x-2.5 bottom-2.5 flex items-end justify-between gap-2">
            <span className="rounded-lg bg-black/45 px-2 py-1 text-[11px] font-semibold text-white tabular-nums backdrop-blur-md">
              {listing.year}.
            </span>

            {listing.photoCount > 1 && (
              <span className="flex items-center gap-1 rounded-lg bg-black/45 px-2 py-1 text-[11px] font-medium text-white backdrop-blur-md">
                <Camera size={11} />
                {listing.photoCount}
              </span>
            )}
          </div>

          {priceDropped && (
            <span className="absolute top-2.5 left-2.5">
              <Badge tone="glass" className="animate-pop-in">
                <TrendingDown size={12} />
                Snižena cena
              </Badge>
            </span>
          )}
        </div>

        <div className="p-3.5 sm:p-4">
          <div className="flex items-center gap-2.5">
            <BrandMark make={make} size="sm" />
            <div className="min-w-0 flex-1">
              <h3
                className="truncate text-[15px] leading-tight font-semibold text-body"
                title={`${listing.makeName} ${listing.modelName}`}
              >
                {listing.makeName} <span className="text-muted">{listing.modelName}</span>
              </h3>
              <p className="truncate text-[11px] text-faint">
                {listing.variant || labelFor(reference?.enums.bodyTypes, listing.bodyType)}
              </p>
            </div>
          </div>

          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-display text-xl font-bold tracking-tight text-body tabular-nums">
              {formatPrice(listing.price)}
            </span>
            {priceDropped && (
              <span className="text-xs text-faint line-through tabular-nums">
                {formatPrice(listing.previousPrice!)}
              </span>
            )}
            <RatingBadge value={listing.rating} count={listing.ratingCount} className="ml-auto" />
          </div>

          {/*
            Mileage, fuel and gearbox. This is the set a buyer scans to decide whether to open a
            listing at all, so it stays on the card and everything else does not.
          */}
          <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-muted">
            <div className="flex items-center gap-1.5">
              <Gauge size={13} className="shrink-0 text-faint" />
              <span className="truncate tabular-nums">{formatMileage(listing.mileage)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Settings2 size={13} className="shrink-0 text-faint" />
              <span className="truncate">
                {labelFor(reference?.enums.transmissions, listing.transmission)}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Fuel size={13} className="shrink-0 text-faint" />
              <span className="truncate">
                {labelFor(reference?.enums.fuelTypes, listing.fuelType)}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <CarSilhouette
                bodyType={listing.bodyType}
                className="w-4 shrink-0 text-faint"
              />
              <span className="truncate">
                {labelFor(reference?.enums.bodyTypes, listing.bodyType)}
              </span>
            </div>
          </dl>

          <div className="mt-3 flex items-center justify-between border-t border-default pt-3 text-xs text-faint">
            <span className="flex items-center gap-1 truncate">
              <MapPin size={12} className="shrink-0" />
              {listing.cityName}
            </span>
            <span className="shrink-0">{formatRelative(listing.publishedAt)}</span>
          </div>
        </div>
      </Link>

      {/*
        Outside the Link, not inside it. A button nested in an anchor is invalid HTML and, more to
        the point, saving a car would navigate away from the results the buyer is still reading.
      */}
      {user && (
        <button
          type="button"
          onClick={() => toggleFavourite.mutate()}
          disabled={toggleFavourite.isPending}
          aria-label={listing.isFavourite ? 'Ukloni iz sačuvanih' : 'Sačuvaj oglas'}
          aria-pressed={listing.isFavourite}
          className={cn(
            'press absolute top-2.5 right-2.5 flex size-9 items-center justify-center rounded-full',
            'bg-black/40 text-white backdrop-blur-md transition duration-300',
            'hover:bg-black/65 disabled:opacity-60',
          )}
        >
          <Heart
            size={16}
            className={cn(
              'transition duration-300',
              listing.isFavourite && 'scale-110 fill-red-500 text-red-500',
            )}
          />
        </button>
      )}
    </article>
  );
}

export function CarCardSkeleton({ index = 0 }: { index?: number }) {
  return (
    <div
      style={{ '--i': Math.min(index, 11) } as React.CSSProperties}
      className="animate-fade-in stagger overflow-hidden rounded-[var(--radius-card)] border border-default bg-raised"
    >
      <div className="skeleton aspect-[4/3]" />
      <div className="space-y-3 p-4">
        <div className="flex items-center gap-2.5">
          <div className="skeleton size-7 rounded-[8px]" />
          <div className="skeleton h-4 flex-1 rounded" />
        </div>
        <div className="skeleton h-6 w-1/2 rounded" />
        <div className="grid grid-cols-2 gap-2">
          <div className="skeleton h-3 rounded" />
          <div className="skeleton h-3 rounded" />
          <div className="skeleton h-3 rounded" />
          <div className="skeleton h-3 rounded" />
        </div>
      </div>
    </div>
  );
}
